import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Button,
  Skeleton,
  Chip,
  Stack,
  Tooltip,
} from '@mui/material';
import FolderDocumentsTable from './FolderDocumentsTable';
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Description as DocumentIcon,
  MoreVert as MoreVertIcon,
  RemoveCircleOutline as RemoveIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  DriveFileMove as MoveIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

const FolderCard = ({ folder, onOpen, onEdit, canManage }) => {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <FolderIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6" component="div" noWrap>
            {folder.name}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {folder.description || 'No description'}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Chip 
            label={`${folder.itemCount || 0} items`} 
            size="small" 
            variant="outlined"
          />
          <Chip 
            label={`Depth: ${folder.depth || 0}`} 
            size="small" 
            variant="outlined"
          />
        </Stack>
      </CardContent>
      <CardActions>
        <Button size="small" onClick={() => onOpen(folder.id, folder.name)}>
          Open
        </Button>
        {canManage && (
          <IconButton size="small" onClick={() => onEdit(folder)}>
            <MoreVertIcon />
          </IconButton>
        )}
      </CardActions>
    </Card>
  );
};

const DocumentCard = ({ document, onView, onRemove, canManage }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED': return 'success';
      case 'PENDING': return 'warning';
      case 'EXPIRED': return 'error';
      case 'REVIEW': return 'info';
      default: return 'default';
    }
  };
  
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <DocumentIcon color="action" sx={{ mr: 1 }} />
          <Typography variant="body1" component="div" noWrap>
            {document.name}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Type: {document.documentType || document.type || 'Document'}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Chip 
            label={document.status} 
            size="small" 
            color={getStatusColor(document.status)}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Added: {format(new Date(document.addedAt || document.createdAt), 'MMM dd, yyyy')}
        </Typography>
      </CardContent>
      <CardActions>
        <Tooltip title="View Document">
          <IconButton size="small" onClick={() => onView(document)}>
            <ViewIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download">
          <IconButton size="small">
            <DownloadIcon />
          </IconButton>
        </Tooltip>
        {canManage && (
          <Tooltip title="Remove from Folder">
            <IconButton size="small" onClick={() => onRemove(document.id)}>
              <RemoveIcon />
            </IconButton>
          </Tooltip>
        )}
      </CardActions>
    </Card>
  );
};

const FolderContents = ({ 
  folderID, 
  contents, 
  documents,
  onNavigateToFolder, 
  canManage,
  loading,
  onRemoveDocument,
  onViewDocument,
  onDownloadDocument 
}) => {
  console.log('FolderContents - folderID:', folderID, 'contents:', contents);
  
  // Separate folders and documents
  const subFolders = contents.filter(item => item.type === 'folder');
  const folderDocuments = contents.filter(item => item.type === 'document');
  
  console.log('Filtered - subFolders:', subFolders, 'folderDocuments:', folderDocuments);
  
  const handleRemoveDocument = async (document) => {
    if (!window.confirm(`Remove "${document.name}" from this folder?`)) {
      return;
    }
    if (onRemoveDocument) {
      onRemoveDocument(document, folderID);
    }
  };
  
  const handleViewDocument = (document) => {
    if (onViewDocument) {
      onViewDocument(document);
    }
  };
  
  const handleDownloadDocument = (document) => {
    if (onDownloadDocument) {
      onDownloadDocument(document);
    }
  };
  
  if (loading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Loading...
        </Typography>
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map(i => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
              <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 1 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }
  
  if (!folderID) {
    // Root level - show only folders
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Root Folders
        </Typography>
        {subFolders.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <FolderIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No folders in root directory
            </Typography>
            {canManage && (
              <Typography variant="body2" color="text.secondary">
                Click "New Folder" to create your first folder
              </Typography>
            )}
          </Box>
        ) : (
          <Grid container spacing={2}>
            {subFolders.map(folder => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={folder.id}>
                <FolderCard 
                  folder={folder} 
                  onOpen={onNavigateToFolder}
                  onEdit={(f) => console.log('Edit folder:', f)}
                  canManage={canManage}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  }
  
  // Inside a folder - show subfolders and documents
  return (
    <Box>
      {/* Subfolders Section */}
      {subFolders.length > 0 && (
        <>
          <Typography variant="h6" gutterBottom>
            Folders
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {subFolders.map(folder => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={folder.id}>
                <FolderCard 
                  folder={folder} 
                  onOpen={onNavigateToFolder}
                  onEdit={(f) => console.log('Edit folder:', f)}
                  canManage={canManage}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}
      
      {/* Documents Section */}
      <Typography variant="h6" gutterBottom>
        Documents ({folderDocuments.length})
      </Typography>
      <FolderDocumentsTable
        documents={folderDocuments}
        loading={false}
        onView={handleViewDocument}
        onDownload={handleDownloadDocument}
        onRemove={handleRemoveDocument}
        canManage={canManage}
      />
    </Box>
  );
};

export default FolderContents;